import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { GUIDE_TOUR_MOBILE_MEDIA_QUERY } from './config'
import type { GuideTourLaunchMode } from './provider/types'
import { useGuideTour } from './provider/guideTourContext'
import {
  findGuideTourTargetElement,
  resolveGuideTourTargetRect,
  GUIDE_TOUR_TARGET_MISSING_DELAY_MS,
  type SpotlightBounds,
} from './overlay/utils'
import {
  getGuideTourStepRoute,
  isGuideTourStepOnPathname,
} from './routing/utils'
import {
  getGuideTourStepModalVisibleGate,
  useGuideTourActivation,
  getGuideTourStepGateOptions,
  isGuideTourStepGateReady,
} from './routing/activation'
import type {
  GuideTourDefinition,
  GuideTourModalVisibleGate,
  GuideTourRuntimeState,
  GuideTourStep,
} from './types'
import { getCurrentGuideTourStep } from './state'
import { mapGuideTourInProgressNotification } from './notifications/utils'
import type { GuideTourInProgressNotification } from './notifications/types'
import {
  registerGuideTourModalCloseHandler,
  GUIDE_TOUR_REQUEST_CLOSE_MODALS_EVENT,
} from './utils/modals'
import { logGuideTourDebug } from './utils/tourRuntime'

export const GUIDE_TOUR_MODAL_TARGET_SETTLE_MS = 400

export const GUIDE_TOUR_UNMEASURABLE_TARGET_SKIP_MS = 1_200

/** Max wait when the target exists but the spotlight cannot be revealed. */
export const GUIDE_TOUR_UNREACHABLE_TARGET_SKIP_MS = 2_500

/** Max wait for modal gates before evaluating whether the step target is absent. */
export const GUIDE_TOUR_MODAL_GATE_MAX_WAIT_MS = 2_500

export const GUIDE_TOUR_MISSING_TARGET_POLL_MS = 100

export type GuideTourTargetAvailability =
  | 'available'
  | 'missing'
  | 'unmeasurable'

export const expectsGuideTourModalForStep = (
  modalVisibleGate: GuideTourModalVisibleGate
): boolean => modalVisibleGate !== undefined && modalVisibleGate !== false

const isGuideTourTargetHidden = (element: HTMLElement): boolean => {
  if (!element.isConnected) {
    return true
  }

  if (element.hidden || element.getAttribute('aria-hidden') === 'true') {
    return true
  }

  const style = window.getComputedStyle(element)

  return style.display === 'none' || style.visibility === 'hidden'
}

export const getGuideTourTargetAvailability = (
  targetId: string,
  root: ParentNode = document
): GuideTourTargetAvailability => {
  const element = findGuideTourTargetElement(targetId, root)

  if (!element || isGuideTourTargetHidden(element)) {
    return 'missing'
  }

  const rect = resolveGuideTourTargetRect(element)

  if (rect.width > 0 && rect.height > 0) {
    return 'available'
  }

  return 'unmeasurable'
}

/** True when the target exists in the DOM, even if not yet measurable (e.g. Hexa hosts). */
export const isGuideTourTargetPresentInDom = (
  targetId: string,
  root: ParentNode = document
): boolean => getGuideTourTargetAvailability(targetId, root) !== 'missing'

export const shouldSkipUnavailableGuideTourTarget = (
  targetId: string,
  root: ParentNode = document
): boolean => getGuideTourTargetAvailability(targetId, root) !== 'available'

export const shouldAutoSkipUnreachableGuideTourStep = ({
  targetId,
  targetBounds,
  availability,
  startedAt,
  now = Date.now(),
}: {
  targetId: string | null | undefined
  targetBounds: unknown
  availability: GuideTourTargetAvailability
  startedAt: number
  now?: number
}): boolean => {
  const normalizedTargetId = targetId?.trim()

  if (!normalizedTargetId || targetBounds) {
    return false
  }

  if (availability === 'missing') {
    return false
  }

  return now - startedAt >= GUIDE_TOUR_UNREACHABLE_TARGET_SKIP_MS
}

export type GuideTourUnavailableStepSkipKind =
  | 'missing'
  | 'unmeasurable'
  | 'unreachable'

export const shouldAutoSkipMissingGuideTourStep = ({
  tour,
  stepIndex,
  pathname,
  targetId,
  root = document,
}: {
  tour: GuideTourDefinition
  stepIndex: number
  pathname: string
  targetId: string | null | undefined
  root?: ParentNode
}): boolean => {
  const normalizedTargetId = targetId?.trim()

  if (!normalizedTargetId) {
    return false
  }

  const step = tour.steps[stepIndex]
  const route = step ? getGuideTourStepRoute(tour, step) : undefined
  const modalVisibleGate = getGuideTourStepModalVisibleGate(tour, stepIndex)

  if (
    route &&
    !expectsGuideTourModalForStep(modalVisibleGate) &&
    !isGuideTourStepOnPathname(tour, stepIndex, pathname)
  ) {
    return false
  }

  return getGuideTourTargetAvailability(normalizedTargetId, root) === 'missing'
}

export const shouldAutoSkipUnavailableGuideTourStep = ({
  tour,
  stepIndex,
  pathname,
  targetId,
  skipKind,
  root = document,
}: {
  tour: GuideTourDefinition
  stepIndex: number
  pathname: string
  targetId: string | null | undefined
  skipKind: GuideTourUnavailableStepSkipKind
  root?: ParentNode
}): boolean => {
  const normalizedTargetId = targetId?.trim()

  if (!normalizedTargetId) {
    return false
  }

  if (skipKind === 'unreachable' || skipKind === 'unmeasurable') {
    const step = tour.steps[stepIndex]
    const route = step ? getGuideTourStepRoute(tour, step) : undefined
    const modalVisibleGate = getGuideTourStepModalVisibleGate(tour, stepIndex)

    if (
      route &&
      !expectsGuideTourModalForStep(modalVisibleGate) &&
      !isGuideTourStepOnPathname(tour, stepIndex, pathname)
    ) {
      return false
    }

    return true
  }

  return shouldAutoSkipMissingGuideTourStep({
    tour,
    stepIndex,
    pathname,
    targetId,
    root,
  })
}

/** @deprecated Use shouldSkipUnavailableGuideTourTarget */
export const shouldSkipMissingGuideTourTarget = (
  targetId: string,
  root: ParentNode = document
): boolean => getGuideTourTargetAvailability(targetId, root) === 'missing'

export const canEvaluateMissingGuideTourTarget = ({
  modalVisibleGate,
  isStepGateReady,
  startedAt,
  targetId,
  now = Date.now(),
}: {
  modalVisibleGate: GuideTourModalVisibleGate
  isStepGateReady: boolean
  startedAt: number
  targetId?: string | null
  now?: number
}): boolean => {
  if (targetId && getGuideTourTargetAvailability(targetId) === 'missing') {
    return now - startedAt >= GUIDE_TOUR_TARGET_MISSING_DELAY_MS
  }

  if (!expectsGuideTourModalForStep(modalVisibleGate)) {
    return isStepGateReady
  }

  if (isStepGateReady) {
    return true
  }

  return now - startedAt >= GUIDE_TOUR_MODAL_GATE_MAX_WAIT_MS
}

export const getUnavailableGuideTourTargetSkipDelayMs = ({
  modalVisibleGate,
  availability,
}: {
  modalVisibleGate: GuideTourModalVisibleGate
  availability: GuideTourTargetAvailability
}): number => {
  if (availability === 'unmeasurable') {
    return GUIDE_TOUR_UNMEASURABLE_TARGET_SKIP_MS
  }

  if (availability === 'missing') {
    return expectsGuideTourModalForStep(modalVisibleGate)
      ? GUIDE_TOUR_MODAL_TARGET_SETTLE_MS
      : GUIDE_TOUR_TARGET_MISSING_DELAY_MS
  }

  return GUIDE_TOUR_TARGET_MISSING_DELAY_MS
}

/** @deprecated Use getUnavailableGuideTourTargetSkipDelayMs */
export const getMissingGuideTourTargetDelayMs = (
  modalVisibleGate: GuideTourModalVisibleGate
): number =>
  expectsGuideTourModalForStep(modalVisibleGate)
    ? GUIDE_TOUR_MODAL_TARGET_SETTLE_MS
    : GUIDE_TOUR_TARGET_MISSING_DELAY_MS

export const createGuideTourUnavailableStepSkipper = ({
  getState,
  advance,
  completeLastStep,
  pathname,
  onRecordSkippedStep,
  onStepAutoSkipped,
  onSkippedStepsWillComplete,
}: {
  getState: () => GuideTourRuntimeState
  advance: () => void
  completeLastStep: (tour: GuideTourDefinition, stepIndex: number) => void
  pathname: string
  onRecordSkippedStep?: (
    stepIndex: number,
    skipKind: GuideTourUnavailableStepSkipKind
  ) => void
  onStepAutoSkipped?: (input: {
    stepIndex: number
    skipKind: GuideTourUnavailableStepSkipKind
  }) => void
  onSkippedStepsWillComplete?: (input: {
    stepIndex: number
    skipKind: GuideTourUnavailableStepSkipKind
  }) => boolean
}): ((skipKind?: GuideTourUnavailableStepSkipKind) => void) => {
  const skipUnavailableStep = (
    skipKind: GuideTourUnavailableStepSkipKind = 'missing'
  ): void => {
    const currentState = getState()
    const tour = currentState.activeTour
    const stepIndex = currentState.currentStepIndex
    const step = getCurrentGuideTourStep(currentState)

    if (!tour || currentState.status !== 'active' || !step) {
      return
    }

    if (
      !shouldAutoSkipUnavailableGuideTourStep({
        tour,
        stepIndex,
        pathname,
        targetId: step.targetId,
        skipKind,
      })
    ) {
      return
    }

    onRecordSkippedStep?.(stepIndex, skipKind)

    if (
      step.forceClick &&
      (skipKind === 'unmeasurable' || skipKind === 'unreachable')
    ) {
      if (
        getState().viewedStepCount === 0 &&
        onSkippedStepsWillComplete?.({
          stepIndex,
          skipKind,
        })
      ) {
        return
      }

      return
    }

    if (step.forceClick && skipKind === 'missing') {
      if (
        onSkippedStepsWillComplete?.({
          stepIndex,
          skipKind,
        })
      ) {
        return
      }
    }

    const willComplete = stepIndex === tour.steps.length - 1 && !step.forceClick

    if (willComplete) {
      if (
        onSkippedStepsWillComplete?.({
          stepIndex,
          skipKind,
        })
      ) {
        return
      }

      completeLastStep(tour, stepIndex)
    }

    onStepAutoSkipped?.({ stepIndex, skipKind })

    const skippedStepIndex = stepIndex
    advance()

    queueMicrotask(() => {
      const nextState = getState()

      if (
        nextState.status !== 'active' ||
        nextState.currentStepIndex <= skippedStepIndex ||
        !nextState.activeTour
      ) {
        return
      }

      const nextStep = getCurrentGuideTourStep(nextState)

      if (
        !shouldAutoSkipUnavailableGuideTourStep({
          tour: nextState.activeTour,
          stepIndex: nextState.currentStepIndex,
          pathname,
          targetId: nextStep?.targetId,
          skipKind: 'missing',
        })
      ) {
        return
      }

      skipUnavailableStep('missing')
    })
  }

  return skipUnavailableStep
}

type UseGuideTourMissingTargetSkipOptions = {
  enabled: boolean
  targetId: string | null
  targetBounds: SpotlightBounds | null
  stepIndex: number
  isStepGateReady: boolean
  modalVisibleGate: GuideTourModalVisibleGate
  allowUnreachableAutoSkip?: boolean
  onSkip: (skipKind: GuideTourUnavailableStepSkipKind) => void
}

/** Advances the tour when a targeted step element is absent or unusable in the DOM. */
export const useGuideTourMissingTargetSkip = ({
  enabled,
  targetId,
  targetBounds,
  stepIndex,
  isStepGateReady,
  modalVisibleGate,
  allowUnreachableAutoSkip = true,
  onSkip,
}: UseGuideTourMissingTargetSkipOptions): void => {
  useEffect(() => {
    if (!enabled || !targetId) {
      return undefined
    }

    let cancelled = false
    const startedAt = Date.now()
    let skipTimeoutId: number | undefined

    const clearSkipTimeout = () => {
      if (skipTimeoutId !== undefined) {
        window.clearTimeout(skipTimeoutId)
        skipTimeoutId = undefined
      }
    }

    const evaluate = () => {
      if (cancelled) {
        return
      }

      const availability = getGuideTourTargetAvailability(targetId)

      if (availability === 'available' && targetBounds) {
        clearSkipTimeout()
        return
      }

      if (
        allowUnreachableAutoSkip &&
        !targetBounds &&
        isStepGateReady &&
        availability !== 'missing' &&
        shouldAutoSkipUnreachableGuideTourStep({
          targetId,
          targetBounds,
          availability,
          startedAt,
        })
      ) {
        clearSkipTimeout()

        if (import.meta.env.DEV) {
          console.warn(
            `[Guide Tour] Target "${targetId}" could not be revealed (${availability}). Skipping step.`
          )
        }

        onSkip('unreachable')
        return
      }

      if (
        !canEvaluateMissingGuideTourTarget({
          modalVisibleGate,
          isStepGateReady,
          startedAt,
          targetId,
        })
      ) {
        return
      }

      if (skipTimeoutId !== undefined) {
        return
      }

      skipTimeoutId = window.setTimeout(() => {
        if (cancelled) {
          return
        }

        skipTimeoutId = undefined

        if (!shouldSkipUnavailableGuideTourTarget(targetId)) {
          return
        }

        if (import.meta.env.DEV) {
          console.warn(
            `[Guide Tour] Target unavailable for "${targetId}" (${availability}). Skipping step.`
          )
        }

        onSkip(availability === 'unmeasurable' ? 'unmeasurable' : 'missing')
      }, getUnavailableGuideTourTargetSkipDelayMs({ modalVisibleGate, availability }))
    }

    evaluate()

    const pollIntervalId = window.setInterval(
      evaluate,
      GUIDE_TOUR_MISSING_TARGET_POLL_MS
    )

    return () => {
      cancelled = true
      window.clearInterval(pollIntervalId)
      clearSkipTimeout()
    }
  }, [
    enabled,
    targetId,
    targetBounds,
    stepIndex,
    isStepGateReady,
    modalVisibleGate,
    allowUnreachableAutoSkip,
    onSkip,
  ])
}

const GUIDE_TOUR_STEP_READINESS_POLL_MS = 250

export const useGuideTourStepReadiness = (
  step: GuideTourStep | null,
  tour: GuideTourDefinition | null,
  stepIndex: number,
  enabled: boolean
): boolean => {
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    if (!enabled || !step || !tour) {
      setIsReady(false)
      return undefined
    }

    const gateOptions = getGuideTourStepGateOptions(tour, stepIndex)

    const checkReady = () => {
      setIsReady(isGuideTourStepGateReady(step, gateOptions))
    }

    checkReady()

    const observer = new MutationObserver(checkReady)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden', 'aria-hidden', 'open'],
    })

    const pollIntervalId = window.setInterval(
      checkReady,
      GUIDE_TOUR_STEP_READINESS_POLL_MS
    )

    return () => {
      observer.disconnect()
      window.clearInterval(pollIntervalId)
    }
  }, [enabled, step, tour, stepIndex])

  return isReady
}

export const useGuideTourModalCloseListener = (onClose: () => void): void => {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    const handleClose = () => {
      logGuideTourDebug('useGuideTourModalCloseListener:handleClose')
      onCloseRef.current()
    }

    logGuideTourDebug('useGuideTourModalCloseListener:register')

    const unregisterHandler = registerGuideTourModalCloseHandler(handleClose)

    window.addEventListener(GUIDE_TOUR_REQUEST_CLOSE_MODALS_EVENT, handleClose)

    return () => {
      unregisterHandler()
      window.removeEventListener(
        GUIDE_TOUR_REQUEST_CLOSE_MODALS_EVENT,
        handleClose
      )
    }
  }, [])
}

export const GUIDE_TOUR_MOBILE_VIEWPORT_LISTENER_DEBOUNCE_MS = 100

export const useGuideTourMobileViewport = (): boolean => {
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return false
    }

    return window.matchMedia(GUIDE_TOUR_MOBILE_MEDIA_QUERY).matches
  })

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return undefined
    }

    const mediaQuery = window.matchMedia(GUIDE_TOUR_MOBILE_MEDIA_QUERY)
    let timeoutId: number | undefined

    const syncViewport = () => {
      setIsMobileViewport(mediaQuery.matches)
    }

    const scheduleSync = () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId)
      }

      timeoutId = window.setTimeout(() => {
        syncViewport()
      }, GUIDE_TOUR_MOBILE_VIEWPORT_LISTENER_DEBOUNCE_MS)
    }

    syncViewport()
    mediaQuery.addEventListener('change', scheduleSync)

    return () => {
      mediaQuery.removeEventListener('change', scheduleSync)

      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [])

  return isMobileViewport
}

type UseScreenGuideTourOptions = {
  enabled?: boolean
}

/** Optional manual activation when a tour should not rely on route matching. */
export const useScreenGuideTour = (
  tourId: string,
  { enabled = true }: UseScreenGuideTourOptions = {}
) => {
  const { getGuideTour } = useGuideTour()
  const tour = getGuideTour(tourId)

  useGuideTourActivation(tour, { enabled })
}

type ScreenGuideTourProps = {
  enabled?: boolean
  tourId: string
}

export const ScreenGuideTour = ({
  tourId,
  enabled = true,
}: ScreenGuideTourProps) => {
  useScreenGuideTour(tourId, { enabled })

  return null
}

export type UseGuideTourInProgressNotificationsResult = {
  items: GuideTourInProgressNotification[]
  count: number
  hasNotifications: boolean
  /** @deprecated Use `hasNotifications`. */
  hasInProgressTours: boolean
  resumeTour: (tourId: string) => void
  restartTour: (tourId: string) => void
  launchTour: (tourId: string, mode: GuideTourLaunchMode) => void
}

/**
 * Headless API for guide tour notifications.
 *
 * Use this when the host app already has a notification center and only needs
 * the pending tour list plus resume/restart/start actions.
 */
export const useGuideTourInProgressNotifications =
  (): UseGuideTourInProgressNotificationsResult => {
    const { guideTourNotifications, launchGuideTour } = useGuideTour()

    const items = useMemo(
      () => guideTourNotifications.map(mapGuideTourInProgressNotification),
      [guideTourNotifications]
    )

    const launchTour = useCallback(
      (tourId: string, mode: GuideTourLaunchMode) => {
        launchGuideTour(tourId, mode)
      },
      [launchGuideTour]
    )

    const resumeTour = useCallback(
      (tourId: string) => {
        launchGuideTour(tourId, 'continue')
      },
      [launchGuideTour]
    )

    const restartTour = useCallback(
      (tourId: string) => {
        launchGuideTour(tourId, 'restart')
      },
      [launchGuideTour]
    )

    return {
      items,
      count: items.length,
      hasNotifications: items.length > 0,
      hasInProgressTours: items.length > 0,
      resumeTour,
      restartTour,
      launchTour,
    }
  }
